'use client';

import { useKindeBrowserClient } from '@kinde-oss/kinde-auth-nextjs';
import { LinkTitle } from './components';
import useSWR from 'swr';
import { fetcher } from '@/lib/fetcher';
import { useEffect, useState, useCallback } from 'react';
import {
  InsuranceWithProduct,
  PaginatedInsurances,
} from '@/services/insurances/insurances';
import { formatDate } from '@/lib/date';
import { InsuranceRowItem } from './components/InsuranceRowItem';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { CheckIcon, DefaultBikeIcon } from '@/components/icons';
import { isActiveInsurance } from '@/lib/insurance';
import { XIcon } from 'lucide-react';

const LoadingSkeleton = () => (
  <div className="col-span-1 row-span-1 bg-white rounded-3xl p-4 lg:p-2 lg:pt-6">
    {/* Title bar skeleton */}
    <div className="pl-4 mb-8">
      <div className="h-4 w-64 bg-gray-200 rounded-lg animate-pulse"></div>
    </div>

    <div className="px-4">
      {/* Header bars */}
      <div className="flex justify-between mb-4">
        <div className="h-5 w-48 bg-gray-200 rounded-lg animate-pulse"></div>
        <div className="h-5 w-24 bg-gray-200 rounded-lg animate-pulse"></div>
      </div>

      {/* Insurance row items - simple bars */}
      <div className="space-y-4">
        {/* Row 1 */}
        <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse"></div>

        {/* Row 2 */}
        <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse"></div>

        {/* Row 3 */}
        <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse"></div>

        {/* Row 4 */}
        <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse"></div>

        {/* Status row with green section */}
        <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse flex justify-between items-center pr-4">
          <div className="invisible">Status</div>
        </div>
      </div>
    </div>

    {/* Navigation buttons skeleton */}
    <div className="flex justify-center mt-6 gap-16">
      <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
      <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
    </div>
  </div>
);

export const InsuranceWidget = () => {
  const { accessTokenRaw, isLoading: isKindeLoading } = useKindeBrowserClient();

  const [api, setApi] = useState<CarouselApi>();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadedInsurances, setLoadedInsurances] = useState<
    InsuranceWithProduct[]
  >([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const ITEMS_PER_PAGE = 2;

  // Process new data with proper error handling
  const processNewData = useCallback(
    (newData: PaginatedInsurances) => {
      // If we got fewer items than requested, there are no more pages
      if (
        !newData.items ||
        newData.items.length < ITEMS_PER_PAGE ||
        page >= newData.pages
      ) {
        setHasMore(false);
      }

      // Ensure items is an array before proceeding
      if (Array.isArray(newData.items)) {
        // Combine new items with existing ones, avoiding duplicates
        setLoadedInsurances((prev) => {
          const existingIds = new Set(prev.map((item) => item.insurance.id));
          const uniqueNewItems = newData.items.filter(
            (item) =>
              item && item.insurance && !existingIds.has(item.insurance.id),
          );
          return [...prev, ...uniqueNewItems];
        });
      } else {
        console.error('API response items is not an array:', newData);
      }

      setIsLoadingMore(false);
    },
    [page, ITEMS_PER_PAGE],
  );

  // Use SWR to fetch data
  const {
    data: paginatedData,
    error: insurancesError,
    isLoading: insurancesIsLoading,
  } = useSWR<PaginatedInsurances>(
    accessTokenRaw
      ? [`/v1/insurances?page=${page}&limit=${ITEMS_PER_PAGE}`, accessTokenRaw]
      : null,
    ([url, token]: [string, string]) => fetcher(url, token),
    {
      revalidateOnFocus: false,
      onSuccess: processNewData,
    },
  );

  // Explicitly handle when data changes outside of onSuccess
  useEffect(() => {
    if (paginatedData && !isLoadingMore) {
      processNewData(paginatedData);
    }
  }, [paginatedData, isLoadingMore, processNewData]);

  // Load more data when needed
  const loadMore = useCallback(() => {
    if (hasMore && !insurancesIsLoading && !isLoadingMore) {
      setIsLoadingMore(true);
      setPage((prevPage) => prevPage + 1);
    }
  }, [hasMore, insurancesIsLoading, isLoadingMore]);

  // Handle carousel navigation to load more data when needed
  useEffect(() => {
    if (!api) return;

    const handleSelect = () => {
      const newIndex = api.selectedScrollSnap();

      // If we're near the end of loaded items, load more
      if (
        loadedInsurances.length > 0 &&
        newIndex >= loadedInsurances.length - 1 &&
        hasMore
      ) {
        loadMore();
      }
    };

    api.on('select', handleSelect);
    return () => {
      api.off('select', handleSelect);
    };
  }, [api, loadedInsurances.length, hasMore, loadMore]);

  if (
    isKindeLoading ||
    (insurancesIsLoading && page === 1 && loadedInsurances.length === 0)
  ) {
    return <LoadingSkeleton />;
  }

  if (insurancesError || !accessTokenRaw) {
    return <div>Error occurred</div>;
  }

  return (
    <div className="col-span-1 row-span-1 bg-white rounded-3xl p-4 lg:p-2 lg:pt-6">
      <div className="pl-4">
        <LinkTitle
          title="Bekijk alle fietsverzekeringen"
          href="/fietsverzekeringen"
        />
      </div>

      <div className="flex flex-col items-center w-full">
        <Carousel className="w-full" setApi={setApi}>
          <CarouselContent className="!ml-0 snap-mandatory snap-x">
            {loadedInsurances.length > 0 ? (
              loadedInsurances.map((insurance, index) => (
                <CarouselItem
                  key={insurance.insurance.id || index}
                  className="snap-start md:basis-full lg:basis-full flex justify-center p-4"
                >
                  {/* Centered container with max-width and consistent padding */}
                  <div className="w-full">
                    <div className="flex mb-2 font-bold">
                      <div>
                        {insurance.product?.brand} {insurance.product?.type}
                      </div>
                      <div className="ml-auto">
                        {insurance.insurance?.company}
                      </div>
                    </div>

                    <div className="flex flex-col gap-3">
                      <InsuranceRowItem
                        title="Verzekering"
                        value={<span>{insurance.insurance.company}</span>}
                      />
                      <InsuranceRowItem
                        title="Polisnummer"
                        value={
                          <div>{insurance.insurance?.policyNumber ?? '-'}</div>
                        }
                      />
                      <InsuranceRowItem
                        title="Start datum"
                        value={
                          <div>
                            {formatDate(
                              new Date(insurance.insurance.startDate ?? ''),
                            )}
                          </div>
                        }
                      />
                      <InsuranceRowItem
                        title="Eind datum"
                        value={
                          <div>
                            {insurance.insurance.endDate
                              ? formatDate(
                                  new Date(insurance.insurance.endDate ?? ''),
                                )
                              : '-'}
                          </div>
                        }
                      />
                      <InsuranceRowItem
                        title="Status"
                        value={(() => {
                          const active = isActiveInsurance(insurance);

                          return (
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium whitespace-nowrap ${
                                active
                                  ? 'text-[var(--success)] bg-green-100'
                                  : 'text-[var(--error)] bg-red-100'
                              }`}
                            >
                              <span className="mr-2 h-8 flex justify-center items-center">
                                {active ? <CheckIcon /> : <XIcon />}
                              </span>
                              {active ? 'Actief' : 'Niet-actief'}
                            </span>
                          );
                        })()}
                      />
                    </div>
                  </div>
                </CarouselItem>
              ))
            ) : (
              <CarouselItem className="snap-start md:basis-full lg:basis-full flex justify-center p-4">
                <div className="w-full flex flex-col items-center justify-center py-8">
                  <DefaultBikeIcon className="w-32" />
                  <p className="text-gray-500 text-center font-medium">
                    Geen verzekeringen gevonden
                  </p>
                </div>
              </CarouselItem>
            )}
          </CarouselContent>

          {/* Navigation buttons container positioned at the bottom */}
          <div className="flex justify-center mt-4 gap-16">
            <CarouselPrevious className="static bg-white transform-none" />
            <CarouselNext className="static bg-white transform-none" />
          </div>
        </Carousel>
      </div>
    </div>
  );
};
